import { notFound } from "next/navigation";
import { useState } from "react";
import { Button as RAButton } from "react-aria-components";
import { Heading, Modal, Spinner, Text } from "ui-components";

import { makeToast } from "@/src/components/Toast/Toast";
import {
  useUserDeactivateUser,
  useUserGetProfile,
} from "@/src/hooks/api/generated";
import { useAuth } from "@/src/hooks/useAuth";

import { UpdatePasswordForm } from "./UpdatePassword";
import { UpdateTwoFactorForm } from "./UpdateTwoFactorForm";

export default function MyAccountSettings(): JSX.Element {
  const [isShowModal, setIsShowModal] = useState(false);
  const { data: userData, isLoading, refetch } = useUserGetProfile();
  const { authData, signOut } = useAuth();

  const { mutate: deleteUser, status: deleteAccountStatus } =
    useUserDeactivateUser();

  const handleDeleteAccount = (): void => {
    if (!userData) {
      return;
    }

    deleteUser(
      {
        params: { isHardDelete: "false" },
      },
      {
        onSuccess: () => {
          signOut();
          makeToast({
            title: "Account deleted successfully.",
            intent: "success",
          });
        },
        onError: () => {
          makeToast({
            title: "Something went wrong.",
            intent: "danger",
          });
        },
      }
    );
    setIsShowModal(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center mt-10">
        <Spinner size="lg" intent="brand-primary" />
      </div>
    );
  }

  if (!userData) {
    return notFound();
  }

  return (
    <div className="w-full flex flex-col gap-y-20">
      <Modal
        animatedModalClassName="md:min-w-[600px] p-10 flex flex-1 items-center justify-center"
        isOpen={isShowModal}
        onOpenChange={setIsShowModal}
      >
        {isShowModal && (
          <div className="flex flex-1 flex-col items-center gap-5">
            <Heading align="center">
              Are you sure you would like to delete your account?
            </Heading>
            <div className="flex flex-col  items-center">
              <Text>Once you delete your account, this</Text>
              <Text>action cannot be undone.</Text>
            </div>
            <RAButton type="button" onPress={handleDeleteAccount}>
              {deleteAccountStatus === "pending" ? (
                <Spinner />
              ) : (
                <Text className="underline">Delete Account</Text>
              )}
            </RAButton>
          </div>
        )}
      </Modal>
      <UpdatePasswordForm userData={userData} />

      <UpdateTwoFactorForm userData={userData} refetch={refetch} />

      {authData?.type !== "agent" && (
        <div className="flex flex-col items-center gap-y-5 mt-20">
          <Heading variant="h3">Delete your account</Heading>

          <RAButton type="button" onPress={() => setIsShowModal(true)}>
            <Text className="underline">Delete Account</Text>
          </RAButton>
        </div>
      )}
    </div>
  );
}
